import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CalendarDays, ChevronRight, Info, RefreshCw, Search, X } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
import AdSlot from '../components/advertisement/AdSlot';
import DashboardStatCards from '../components/dashboard/DashboardStatCards';
import IpoCalendarCard from '../components/dashboard/IpoCalendarCard';
import SubscriptionTrackerCard from '../components/dashboard/SubscriptionTrackerCard';
import IpoDocumentsCard from '../components/dashboard/IpoDocumentsCard';
import BrokerRatingsCard from '../components/dashboard/BrokerRatingsCard';
import AlertsSignupStrip from '../components/dashboard/AlertsSignupStrip';
import {
  DASHBOARD_BOARDS,
  buildBrokerRatingRows,
  buildDashboardDocuments,
  buildDashboardSubscriptions,
  buildMainboardIpoRows,
  catalogIdsFor,
  computeDashboardStats,
  filterDashboardRows,
  toDashboardIpos,
} from '../utils/ipoDashboard';
import { buildSmeIpoRows, isSameCompany } from '../utils/smeIpos';
import { buildDrhpRows } from '../utils/drhpTracker';
import { formatRefreshedAt } from '../utils/gmpBoard';
import { todayIso } from '../utils/ipoCalendar';
import { fetchGmp, selectGmpBoardRows } from '../store/slices/gmpSlice';
import {
  fetchSmeSubscriptionSummary,
  fetchSubscriptionSummary,
  selectSmeSubscriptionRows,
  selectSubscriptionRows,
} from '../store/slices/subscriptionSlice';
import { fetchSmeUpcomingIpos, fetchUpcomingIpos, selectOpeningSoonIpos, selectSmeOpeningSoonIpos } from '../store/slices/upcomingIposSlice';
import { fetchLiveIpos } from '../store/slices/ipoSlice';
import { fetchSmeIpos } from '../store/slices/smeIposSlice';
import { fetchSmeReviews } from '../store/slices/smeReviewsSlice';
import { fetchDrhpDetails, fetchDrhpRegistry } from '../store/slices/drhpSlice';
import { fetchCatalogIndex } from '../store/slices/ipoCatalogSlice';
import { fetchBrokerReviews, selectBrokerReviewsLoading } from '../store/slices/brokerReviewsSlice';

const PAGE_TITLE = 'IPO Dashboard 2026 — Mainboard & SME IPOs | Alambrix IPO';
const NOTICE_MS = 4000;
const FEED_MAX_AGE_MS = 60000;
const CATALOG_MAX_AGE_MS = 5 * 60 * 1000;
const REVIEWS_MAX_AGE_MS = 10 * 60 * 1000;

const settled = (status) => status === 'succeeded' || status === 'failed';

export const IpoDashboardPage = () => {
  const dispatch = useDispatch();
  const today = useMemo(() => todayIso(), []);
  const [boardKey, setBoardKey] = useState('mainboard');
  const [notice, setNotice] = useState(null);
  // Company search across the four tables; the headline stats stay whole-market
  const [query, setQuery] = useState('');
  const board = DASHBOARD_BOARDS[boardKey];

  // IPOs: GMP board + NSE schedules (mainboard), GET /api/core/ipos/sme (SME)
  const gmpRows = useSelector(selectGmpBoardRows);
  const gmpStatus = useSelector((state) => state.gmp.status);
  const gmpRefreshedAt = useSelector((state) => state.gmp.refreshedAt);
  const scheduleRows = useSelector(selectOpeningSoonIpos);
  const smeScheduleRows = useSelector(selectSmeOpeningSoonIpos);
  const smeScheduleItems = useSelector((state) => state.smeUpcomingIpos.items);
  const upcomingStatus = useSelector((state) => state.upcomingIpos.status);
  const liveCurrent = useSelector((state) => state.ipo.liveIposCurrent);
  const liveUpcoming = useSelector((state) => state.ipo.liveIposUpcoming);
  const liveLoaded = useSelector((state) => Boolean(state.ipo.liveIposRefreshedAt || state.ipo.liveIposError));
  const liveLoading = useSelector((state) => state.ipo.liveIposLoading);
  const smeList = useSelector((state) => state.smeIpos.items);
  const smeListStatus = useSelector((state) => state.smeIpos.status);
  // Subscription: GET /api/market/subscription (+ /sme)
  const subscriptionRows = useSelector(selectSubscriptionRows);
  const smeSubscriptionRows = useSelector(selectSmeSubscriptionRows);
  const subscriptionStatus = useSelector((state) => state.subscription.status);
  const smeSubscriptionStatus = useSelector((state) => state.smeSubscription.status);
  // Reviews: per-IPO broker reviews (mainboard), GET /api/core/ipos/sme/reviews (SME)
  const reviewsById = useSelector((state) => state.brokerReviews.byId);
  const reviewsLoading = useSelector(selectBrokerReviewsLoading);
  const smeReviewItems = useSelector((state) => state.smeReviews.items);
  const smeReviewsStatus = useSelector((state) => state.smeReviews.status);
  // Catalog ids for the broker reviews: GET /api/core/ipos (list only)
  const catalogRecords = useSelector((state) => state.ipoCatalog.index.records);
  const catalogIndexStatus = useSelector((state) => state.ipoCatalog.index.status);
  // Documents: GET /api/core/ipos/drhp-tracker
  const trackerRecords = useSelector((state) => state.drhp.records);
  const trackerDetails = useSelector((state) => state.drhp.detailsById);
  const trackerStatus = useSelector((state) => state.drhp.status);

  const loadFeeds = (force = false) => {
    const options = force ? {} : { maxAgeMs: FEED_MAX_AGE_MS };
    dispatch(fetchGmp(options));
    dispatch(fetchUpcomingIpos(options));
    dispatch(fetchSmeUpcomingIpos(options));
    dispatch(fetchSmeIpos(options));
    dispatch(fetchSubscriptionSummary(options));
    dispatch(fetchSmeSubscriptionSummary(options));
    dispatch(fetchSmeReviews(options));
    dispatch(fetchDrhpRegistry(force ? {} : { maxAgeMs: CATALOG_MAX_AGE_MS }));
    dispatch(fetchCatalogIndex(force ? {} : { maxAgeMs: CATALOG_MAX_AGE_MS }));
    if (force || (!liveLoaded && !liveLoading)) dispatch(fetchLiveIpos(force));
  };

  useEffect(() => {
    loadFeeds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const ipos = useMemo(() => {
    const mainboardRows = buildMainboardIpoRows(
      { gmpRows, subscriptionRows, liveItems: [...liveCurrent, ...liveUpcoming], scheduleRows },
      today
    );
    const smeRows = buildSmeIpoRows({ smeList, liveIpos: smeScheduleItems, gmpRows, subscriptionRows: smeSubscriptionRows }, today);
    return {
      mainboard: toDashboardIpos(mainboardRows, 'mainboard', today),
      sme: toDashboardIpos(smeRows, 'sme', today),
    };
  }, [gmpRows, subscriptionRows, liveCurrent, liveUpcoming, scheduleRows, smeList, smeScheduleItems, smeSubscriptionRows, today]);

  const subscriptions = useMemo(() => {
    const rows = [...subscriptionRows, ...smeSubscriptionRows];
    return {
      mainboard: buildDashboardSubscriptions(rows, 'mainboard', { limit: 50, today }),
      sme: buildDashboardSubscriptions(rows, 'sme', { limit: 50, today }),
    };
  }, [subscriptionRows, smeSubscriptionRows, today]);

  // Broker reviews for the mainboard issues the catalog knows
  const reviewTargets = useMemo(() => catalogIdsFor(ipos.mainboard, catalogRecords), [ipos.mainboard, catalogRecords]);
  const reviewIds = useMemo(() => [...new Set(reviewTargets.flatMap((target) => target.ids))].sort(), [reviewTargets]);
  const reviewIdsKey = reviewIds.join(',');
  useEffect(() => {
    if (reviewIds.length) dispatch(fetchBrokerReviews({ ids: reviewIds, maxAgeMs: REVIEWS_MAX_AGE_MS }));
    // reviewIdsKey stands in for the array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, reviewIdsKey]);

  const ratings = useMemo(() => {
    const mainboard = buildBrokerRatingRows(
      reviewTargets.map(({ ipo, ids }) => ({
        company: ipo.company,
        link: ipo.link,
        payloads: ids.map((id) => reviewsById[id]?.data).filter(Boolean),
      })).filter((entry) => entry.payloads.length)
    );
    const sme = buildBrokerRatingRows(
      smeReviewItems
        .filter((item) => item?.company_name)
        .map((item) => {
          const ipo = ipos.sme.find((candidate) => isSameCompany(candidate.company, item.company_name));
          return { company: ipo?.company || item.company_name, link: ipo?.link || '/sme-ipo-reviews', payloads: [item] };
        })
    );
    return { mainboard, sme };
  }, [reviewTargets, reviewsById, smeReviewItems, ipos.sme]);

  const documentRows = useMemo(
    () => ({ records: trackerRecords, gmpRows, scheduleRows: [...scheduleRows, ...smeScheduleRows] }),
    [trackerRecords, gmpRows, scheduleRows, smeScheduleRows]
  );

  // Which filings the two cards will show. Three quarters of the tracker has an
  // extracted analysis, but only a handful of rows are rendered, so the ids are
  // settled here first and only those details are fetched. Nothing the sort or
  // the board filter reads comes from a detail, so this set does not shift once
  // the details land -- it would otherwise refetch itself in a loop.
  const documentIds = useMemo(() => {
    const rows = buildDrhpRows({ ...documentRows, detailsById: {} });
    return [...buildDashboardDocuments(rows, 'mainboard'), ...buildDashboardDocuments(rows, 'sme')].map((doc) => doc.id);
  }, [documentRows]);
  const documentIdsKey = documentIds.join(',');

  useEffect(() => {
    if (documentIds.length) dispatch(fetchDrhpDetails({ ids: documentIds }));
    // documentIdsKey stands in for the array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, documentIdsKey]);

  // Searched before the three-card limit, so any filing can surface. Details are
  // still fetched only for the default view: a searched filing shows without its
  // lead manager rather than typing triggering requests.
  const documents = useMemo(() => {
    const rows = filterDashboardRows(buildDrhpRows({ ...documentRows, detailsById: trackerDetails }), query);
    return { mainboard: buildDashboardDocuments(rows, 'mainboard'), sme: buildDashboardDocuments(rows, 'sme') };
  }, [documentRows, trackerDetails, query]);

  const boardIpos = ipos[boardKey];
  const boardSubscriptions = subscriptions[boardKey];
  const boardRatings = ratings[boardKey];
  const stats = useMemo(
    () => computeDashboardStats({ ipos: boardIpos, subscriptions: boardSubscriptions, ratings: boardRatings }),
    [boardIpos, boardSubscriptions, boardRatings]
  );
  // Subscriptions are searched before the card's six-row cut for the same reason
  const searchedIpos = useMemo(() => filterDashboardRows(boardIpos, query), [boardIpos, query]);
  const searchedSubscriptions = useMemo(() => filterDashboardRows(boardSubscriptions, query), [boardSubscriptions, query]);
  const searchedRatings = useMemo(() => filterDashboardRows(boardRatings, query), [boardRatings, query]);
  const otherBoardKey = boardKey === 'sme' ? 'mainboard' : 'sme';
  const otherBoardMatches = useMemo(
    () => (query.trim() ? filterDashboardRows(ipos[otherBoardKey], query).length : 0),
    [ipos, otherBoardKey, query]
  );
  const reviewCounts = useMemo(() => Object.fromEntries(boardRatings.map((row) => [row.company, row.total])), [boardRatings]);

  const mainboardIposLoading = !ipos.mainboard.length && !(settled(gmpStatus) && settled(subscriptionStatus) && settled(upcomingStatus));
  const smeIposLoading = !ipos.sme.length && !settled(smeListStatus);
  const iposLoading = boardKey === 'sme' ? smeIposLoading : mainboardIposLoading;
  const iposFailed = boardKey === 'sme' ? smeListStatus === 'failed' : gmpStatus === 'failed' && subscriptionStatus === 'failed';
  const subscriptionsLoading = !boardSubscriptions.length && !(settled(subscriptionStatus) && settled(smeSubscriptionStatus));
  const ratingsLoading =
    !boardRatings.length && (boardKey === 'sme' ? !settled(smeReviewsStatus) : !settled(catalogIndexStatus) || reviewsLoading || mainboardIposLoading);
  const documentsLoading = !documents[boardKey].length && !settled(trackerStatus);
  // A failed feed shows a retry, never an "empty" message
  const subscriptionsFailed = (boardKey === 'sme' ? smeSubscriptionStatus : subscriptionStatus) === 'failed';
  const ratingsFailed = boardKey === 'sme' ? smeReviewsStatus === 'failed' : catalogIndexStatus === 'failed';
  const trackerFailed = trackerStatus === 'failed';

  const retrySubscriptions = () => dispatch(boardKey === 'sme' ? fetchSmeSubscriptionSummary() : fetchSubscriptionSummary());
  const retryRatings = () => dispatch(boardKey === 'sme' ? fetchSmeReviews() : fetchCatalogIndex());
  const isRefreshing = [gmpStatus, subscriptionStatus, smeListStatus, trackerStatus].includes('loading');
  const refreshedLabel = formatRefreshedAt(gmpRefreshedAt);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pb-12 sm:pb-16 space-y-6 sm:space-y-7">
          {/* Partner ad (GET /api/ads): dashboard slot first, then the home leaderboard */}
          <AdSlot slotId={['ipo-dashboard-top', 'leaderboard']} />

          {/* Breadcrumb + page header */}
          <div>
            <nav
              aria-label="Breadcrumb"
              className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap"
            >
              <Link to="/" className="hover:text-[#008080] transition-colors">
                Home
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <Link to="/" className="hover:text-[#008080] transition-colors">
                IPOs
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-slate-900 font-semibold">IPO Dashboard (Mainboard &amp; SME)</span>
            </nav>

            <div className="mt-4 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
              <div className="max-w-2xl">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">IPO Dashboard</h1>
                  <span className="px-2.5 py-1 rounded-full border border-teal-200 bg-teal-50 text-[11px] font-bold text-[#006666]">
                    Live 2026 Data
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  Stay updated on Mainboard and SME IPO activity across NSE &amp; BSE — follow companies as they debut,
                  track subscriptions, broker recommendations, and offer documents.
                  {refreshedLabel && <span className="text-slate-400"> Updated {refreshedLabel}.</span>}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap lg:shrink-0">
                <div
                  role="group"
                  aria-label="Choose IPO board"
                  className="inline-flex p-1 rounded-xl bg-slate-100 border border-slate-200/80"
                >
                  {Object.values(DASHBOARD_BOARDS).map((option) => {
                    const active = option.key === boardKey;
                    const loadingCount = option.key === 'sme' ? smeIposLoading : mainboardIposLoading;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        aria-pressed={active}
                        onClick={() => setBoardKey(option.key)}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${active ? 'bg-white text-[#008080] shadow-sm' : 'text-slate-600 hover:text-slate-900'
                          }`}
                      >
                        {option.label}
                        <span
                          className={`px-1.5 py-px rounded-md text-[10px] ${active ? 'bg-teal-50 text-[#008080]' : 'bg-slate-200/80 text-slate-600'
                            }`}
                        >
                          {loadingCount ? '…' : ipos[option.key].length}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => loadFeeds(true)}
                  disabled={isRefreshing}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <RefreshCw className={`w-3.5 h-3.5 text-slate-400 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                {/* <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 whitespace-nowrap">
                  <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                  Year 2026
                </span> */}
              </div>
            </div>
          </div>

          <DashboardStatCards board={board} stats={stats} loading={iposLoading} />

          <div className="space-y-2">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <label className="relative block w-full sm:max-w-md">
                <span className="sr-only">Search the dashboard by company or NSE symbol</span>
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search IPOs, subscriptions, filings & reviews..."
                  className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 shadow-sm focus:outline-none focus:border-[#008080] focus:ring-2 focus:ring-[#008080]/15"
                />
                {query && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => setQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </label>
              {query.trim() && !iposLoading && (
                <p className="text-xs text-slate-500" role="status">
                  {searchedIpos.length} of {boardIpos.length} {board.label} match “{query.trim()}”
                </p>
              )}
            </div>

            {query.trim() && !iposLoading && !searchedIpos.length && otherBoardMatches > 0 && (
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-teal-200 bg-teal-50 px-4 py-2.5 text-xs text-[#006666]">
                <span>
                  {otherBoardMatches} {DASHBOARD_BOARDS[otherBoardKey].shortLabel} {otherBoardMatches === 1 ? 'IPO matches' : 'IPOs match'} “{query.trim()}”.
                </span>
                <button type="button" onClick={() => setBoardKey(otherBoardKey)} className="font-bold underline hover:no-underline">
                  Show {DASHBOARD_BOARDS[otherBoardKey].label}
                </button>
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-5 sm:gap-6 items-start">
            <div className="space-y-5 sm:space-y-6 min-w-0">
              {/* key resets the status filter when switching boards */}
              <IpoCalendarCard
                key={board.key}
                board={board}
                ipos={searchedIpos}
                query={query}
                reviewCounts={reviewCounts}
                loading={iposLoading}
                error={iposFailed}
                onRetry={() => loadFeeds(true)}
              />
              <SubscriptionTrackerCard
                board={board}
                rows={searchedSubscriptions.slice(0, 6)}
                query={query}
                loading={subscriptionsLoading}
                openCount={stats.open}
                error={subscriptionsFailed}
                onRetry={retrySubscriptions}
              />
              <IpoDocumentsCard
                board={board}
                documents={documents[boardKey]}
                query={query}
                loading={documentsLoading}
                error={trackerFailed}
                onRetry={() => dispatch(fetchDrhpRegistry())}
              />
            </div>
            <div className="space-y-5 sm:space-y-6 min-w-0">
              <BrokerRatingsCard
                board={board}
                rows={searchedRatings}
                query={query}
                loading={ratingsLoading}
                error={ratingsFailed}
                onRetry={retryRatings}
              />
            </div>
          </div>

          {/* <AlertsSignupStrip onUnavailable={setNotice} /> */}
        </div>

        {notice && (
          <div
            role="status"
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md flex items-start gap-2 rounded-xl bg-slate-900 text-white text-xs px-4 py-3 shadow-elevated"
          >
            <Info className="w-4 h-4 flex-shrink-0 text-teal-300" />
            <span className="flex-1">{notice}</span>
            <button
              type="button"
              onClick={() => setNotice(null)}
              aria-label="Dismiss"
              className="text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>
    </>
  );
};

export default IpoDashboardPage;
